import { applyMiddleware, compose, createStore } from 'redux';
import { logger } from 'redux-logger';
import thunkMiddleware from 'redux-thunk';
import navigationReducer from '../reducers/navigationReducer.mjs';
import localQuestionReducer from '../reducers/localQuestionReducer.mjs';
import validationReducer from '../reducers/validationReducer.mjs';
import reduceReducers from '../reducers/reduce-reducers.mjs';
import progressReducer from '../reducers/progressReducer.mjs';
import productReducer from '../reducers/productReducer.mjs';
import contactMeReducer from '../reducers/contactMeReducer.mjs';
import notesReducer from '../reducers/notesReducer.mjs';
import loginReducer from '../reducers/loginReducer.mjs';

let mainReducer = null;
let mainStore = null;
const serviceWaitTimeout = 30000;

const emptyState = {};
Object.freeze(emptyState);

const getEmptyState = async () => emptyState;

/**
 * Format for the state should be {
    user: {
      uuid: string,
      guideId: long,
      guideType: business|consumer|hybrid,
      guideDelivery: branch|online,
      assignment: {
        employee: {

        },
        branch: {

        }
      }
    },
    currentPage: string,
    answers: {
      appkey: {
        appkey: string,
        value: array[string], number, boolean,
        timestamp: date
      }
    },
    pages: {
      urlPath: {

      }
    },
    products: { //This is a productGroup
      name: string,
      products: [
        {

        }
      ],
      groups: []//array of subgroups
    },
    processingNavigation: false,
    runMode: undefined,
    progress: {
      total: number 0-100,
      paths: [],
      reverse: boolean
    }
  }
 * @returns the reducer chain used by this application
 */
const initializeReducer = async (
  {
    reducers = [],
    loadInitialState = getEmptyState,
    isBranchGuide,
    decorateStateFn
  }
) => {
  const INITIAL_STATE = await loadInitialState({ isBranchGuide, decorateStateFn });

  const branchOnlyReducers = INITIAL_STATE.isBranchGuide || isBranchGuide ? [
    notesReducer,
    loginReducer
  ] : [];

  mainReducer = reduceReducers(
    INITIAL_STATE,
    [
      navigationReducer,
      localQuestionReducer,
      validationReducer,
      progressReducer,
      productReducer,
      contactMeReducer,
      ...branchOnlyReducers,
      ...reducers
    ]
  );

  return mainReducer;
};

const initializeStore = async (
  {
    reducers,
    middlewares = [],
    loadInitialState,
    isBranchGuide,
    decorateStateFn
  }
) => {
  const composeEnhancers = (typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__)
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      // Specify extension’s options like name, actionsBlacklist, actionsCreators, serialize...
    })
    : compose;

  const allMiddleWares = [ thunkMiddleware ].concat(middlewares).concat([ logger ]);
  const reducer = await getReducer({
    reducers,
    loadInitialState,
    isBranchGuide,
    decorateStateFn
  });

  mainStore = createStore(
    reducer,
    composeEnhancers(
      applyMiddleware(...allMiddleWares)
    )
  );

  return mainStore;
};

const storeInitialized = () => {
  const waitStart = Date.now();

  return new Promise( (resolve, reject) => {
    const checkForServices = () => {
      console.info('checking for main store availability');
      if (mainStore) {
        console.info('main store is ready');
        return resolve(true);
      }

      const elapsed = Date.now() - waitStart;
      if (elapsed >= serviceWaitTimeout) {
        return reject( new Error('main store is unavailable') );
      }

      console.info('waiting for main store availability');
      setTimeout(() => checkForServices(), 200);
    };

    checkForServices();
  });
};

const getReducer = async (options) => mainReducer || await initializeReducer(options);

const getStore = async (options) => mainStore || await initializeStore(options);

export {
  getReducer,
  getStore,
  storeInitialized
};
